import { FUNDING_PROFILE_INFO_TOP_BAR_ID, TOAST_CONTAINER_ID, TOAST_OFFSET } from "@constants/ui";

/**
 * Calculates the additional margin-top (in px) that a toast needs (relative to the
 * toast container) so that it appears immediately below the funding profile top bar
 * plus a small extra offset. If the funding profile top bar is not present or not visible, returns 0.
 */
export function getToastOffset({
  toastContainerId = TOAST_CONTAINER_ID,
  fundingProfileTopBarId = FUNDING_PROFILE_INFO_TOP_BAR_ID,
  offset = TOAST_OFFSET,
}: {
  toastContainerId?: string;
  fundingProfileTopBarId?: string;
  offset?: number;
}) {
  const topBar = document.getElementById(fundingProfileTopBarId);
  if (!topBar) return 0;

  // Check if the top bar is actually visible (not just present in DOM)
  const computedStyle = window.getComputedStyle(topBar);
  const isVisible = computedStyle.opacity !== "0" && topBar.offsetHeight > 0;
  if (!isVisible) return 0;

  // Prefer querying by configured id; fallback to a well-known class on the container
  const toastContainer =
    document.getElementById(toastContainerId) || (document.querySelector(`.${toastContainerId}`) as HTMLElement | null);
  if (!toastContainer) return 0;

  const topBarRect = topBar.getBoundingClientRect();
  const containerRect = toastContainer.getBoundingClientRect();

  // Desired top edge of the toast content: just below the top bar + extra offset
  const desiredTopWithinViewport = topBarRect.bottom + offset;
  // Convert desired viewport position to a margin relative to the container's top
  const marginRelativeToContainer = desiredTopWithinViewport - containerRect.top;

  return Math.max(0, Math.round(marginRelativeToContainer));
}

/**
 * Checks if a game iframe dialog is currently open by querying the DOM.
 * Game iframes are identified by having `data-is-game="true"` attribute.
 * @returns boolean indicating if a game iframe dialog is currently visible
 */
export function isGameIframeDialogOpen(): boolean {
  const gameIframe = document.querySelector<HTMLIFrameElement>('iframe[data-is-game="true"]');
  if (!gameIframe) return false;

  // Check if the iframe is visible
  const computedStyle = window.getComputedStyle(gameIframe);
  const isVisible =
    computedStyle.display !== "none" &&
    computedStyle.visibility !== "hidden" &&
    computedStyle.opacity !== "0" &&
    gameIframe.offsetWidth > 0 &&
    gameIframe.offsetHeight > 0;

  if (!isVisible) return false;

  // Check if the iframe is within a dialog container (high z-index overlay)
  let parent: HTMLElement | null = gameIframe.parentElement;
  while (parent) {
    const parentStyle = window.getComputedStyle(parent);
    const zIndex = parseInt(parentStyle.zIndex, 10);
    // Check for high z-index values that indicate dialog containers (z-[1030], z-[10000], etc.)
    if (zIndex >= 1000 || parentStyle.position === "fixed" || parentStyle.position === "absolute") {
      return true;
    }
    parent = parent.parentElement;
  }

  // If we can't find a high z-index parent, but the iframe is visible, assume it's open
  return true;
}
